// Form submissions route through the ACL /api/leads/capture endpoint so the
// Lead Guardian 60-second SMS auto-reply fires. On failure the call falls back
// to a direct Supabase anon-insert so no lead is ever lost.

import { supabase } from '@/lib/supabase';
import {
  SOTSVC_SMS_CONSENT_TEXT,
  SOTSVC_SMS_CONSENT_VERSION,
  getConsentSourceUrl,
} from './consent';

const SOTSVC_BRAND_ID = import.meta.env.VITE_SOTSVC_BRAND_ID as string | undefined;
const BRAND_NAME = 'Sonz of Thunder Services';
const ACL_API_URL = (import.meta.env.VITE_ACL_API_URL as string | undefined) || 'https://aicommandlab.com';

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  message: string;
}

export interface QuoteFormData {
  fullName: string;
  email: string;
  phone: string;
  serviceType: string;
  address: string;
  preferredDate: string;
  preferredTime: string;
  smsOptIn: boolean;
}

// DLD-422: bundle TCPA audit-trail fields when the user gave consent. Returned
// shape mirrors what the ACL handler stores on the leads row (consent_text,
// consent_version, consent_source_url + a client-stamped consent_timestamp for
// the fallback insert path; the API path uses the server's timestamp).
function buildConsentPayload(smsOptIn: boolean) {
  if (!smsOptIn) {
    return {
      consent_text: undefined,
      consent_version: undefined,
      consent_source_url: undefined,
      consent_timestamp: undefined,
    };
  }
  return {
    consent_text: SOTSVC_SMS_CONSENT_TEXT,
    consent_version: SOTSVC_SMS_CONSENT_VERSION,
    consent_source_url: getConsentSourceUrl(),
    consent_timestamp: new Date().toISOString(),
  };
}

async function captureViaACL(payload: Record<string, unknown>): Promise<void> {
  const response = await fetch(`${ACL_API_URL}/api/leads/capture`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brand: 'sotsvc', ...payload }),
  });
  if (!response.ok) {
    throw new Error(`ACL API ${response.status}`);
  }
}

async function fallbackInsert(payload: Record<string, unknown>): Promise<void> {
  if (!SOTSVC_BRAND_ID) {
    throw new Error('Missing VITE_SOTSVC_BRAND_ID env var');
  }
  const { error } = await (supabase as any).from('leads').insert({
    brand_id: SOTSVC_BRAND_ID,
    brand_name: BRAND_NAME,
    source: 'website_contact',
    status: 'new',
    ...payload,
  });
  if (error) {
    throw new Error(error.message || 'Submission failed');
  }
}

async function captureLead(
  aclPayload: Record<string, unknown>,
  fallbackPayload: Record<string, unknown>,
): Promise<{ success: boolean }> {
  try {
    await captureViaACL(aclPayload);
  } catch (aclErr) {
    console.warn('[forms] ACL API failed, falling back to direct insert:', aclErr);
    await fallbackInsert(fallbackPayload);
  }
  return { success: true };
}

export async function submitContactForm(
  data: ContactFormData,
  formType: string = 'contact_message',
  smsOptIn: boolean = false,
  hp: string = '',
) {
  const consent = buildConsentPayload(smsOptIn);
  return captureLead(
    {
      // Honeypot. The ACL endpoint scores a filled `_hp` at 100 points, which
      // flags the lead on its own and suppresses every downstream send. It has
      // always read this field; until now nothing on this site rendered it.
      _hp: hp,
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      notes: data.message,
      sms_consent: smsOptIn,
      sms_opt_in: smsOptIn,
      consent_text: consent.consent_text,
      consent_version: consent.consent_version,
      consent_source_url: consent.consent_source_url,
      metadata: { form_type: formType, source_site: 'sotsvc.com' },
    },
    {
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      message: data.message,
      sms_consent: smsOptIn,
      consent_text: consent.consent_text ?? null,
      consent_version: consent.consent_version ?? null,
      consent_source_url: consent.consent_source_url ?? null,
      consent_timestamp: consent.consent_timestamp ?? null,
      metadata: { form_type: formType, source_site: 'sotsvc.com', sms_opt_in: smsOptIn },
    },
  );
}

export async function submitQuoteForm(data: QuoteFormData, hp: string = '') {
  const notes = [
    `Service: ${data.serviceType}`,
    `Address: ${data.address}`,
    `Preferred Date: ${data.preferredDate}`,
    `Preferred Time: ${data.preferredTime}`,
    `SMS Opt-in: ${data.smsOptIn ? 'Yes' : 'No'}`,
  ].join('\n');

  const consent = buildConsentPayload(data.smsOptIn);
  return captureLead(
    {
      _hp: hp,
      name: data.fullName,
      email: data.email,
      phone: data.phone || undefined,
      notes,
      frequency: data.serviceType,
      sms_consent: data.smsOptIn,
      sms_opt_in: data.smsOptIn,
      consent_text: consent.consent_text,
      consent_version: consent.consent_version,
      consent_source_url: consent.consent_source_url,
      metadata: {
        form_type: 'quote_request',
        source_site: 'sotsvc.com',
        address: data.address,
        preferred_date: data.preferredDate,
        preferred_time: data.preferredTime,
      },
    },
    {
      name: data.fullName,
      email: data.email,
      phone: data.phone || null,
      message: notes,
      sms_consent: data.smsOptIn,
      consent_text: consent.consent_text ?? null,
      consent_version: consent.consent_version ?? null,
      consent_source_url: consent.consent_source_url ?? null,
      consent_timestamp: consent.consent_timestamp ?? null,
      metadata: {
        form_type: 'quote_request',
        source_site: 'sotsvc.com',
        service_type: data.serviceType,
        address: data.address,
        preferred_date: data.preferredDate,
        preferred_time: data.preferredTime,
        sms_opt_in: data.smsOptIn,
      },
    },
  );
}
